import mustache from "./mustache.js";

let cart = null;

document.addEventListener("click", async (e) => {
  // Check if the click happened on the element with mvthru-cart-action (or its children)
  const cartActionElement = e.target.closest(
    '[data-mvthru-cart-action="increase"], [data-mvthru-cart-action="decrease"]',
  );

  if (cartActionElement) {
    const cartItem = cartActionElement.closest(
      '[data-mvthru-item="cart-item"]',
    ); // Get the closest .item element
    const quantityEl = cartItem.querySelector(
      '[data-mvthru-cart-value="quantity"]',
    );
    const cartAction = cartActionElement.getAttribute(
      "data-mvthru-cart-action",
    );
    let currentQty = parseInt(quantityEl.textContent, 10);

    // Adjust quantity based on the action
    if (cartAction === "increase") {
      currentQty += 1;
      editItem(cartItem.id, currentQty);
    } else if (cartAction === "decrease") {
      if (currentQty > 1) {
        currentQty -= 1;
        editItem(cartItem.id, currentQty);
      } else {
        currentQty = 0;
        removeItem(cartItem.id);
      }
    }

    if (currentQty === 0) {
      await fetch(`/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: cartItem.id,
          action: "remove",
        }),
      });
    } else {
      await fetch(`/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: cartItem.id,
          action: cartAction,
          quantity: 1,
        }),
      });
    }
  }
});

// States Functionality
export const createState = (initialState) => {
  let state = initialState;
  const listeners = new Set();

  const getState = () => state;

  const setState = (newState) => {
    state = typeof newState === "function" ? newState(state) : newState;
    listeners.forEach((listener) => listener(state));
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { getState, setState, subscribe };
};

// Initlaise Cart
export function initCart() {
  return new Promise(async (resolve) => {
    cart = createState();
    cart.subscribe(updateCartUI);

    await fetchCart();

    resolve();
  });
}

// Function to render cart items
const updateCartUI = () => {
  if (!cart.getState()) return; // Ensure cart state exists

  const cartState = cart.getState();
  const items = cartState?.items || [];

  const cartElement = document.querySelector('[data-mvthru-view="cart"]');

  if (cartElement) {
    const storeData = document.querySelector(`#mvthru-data-store`).innerHTML;

    const cartUI = document.querySelector(`#mvthru_extracted_cart`).innerHTML;
    const cartEmptyUI = document.querySelector(
      `#mvthru_extracted_empty_cart`,
    ).innerHTML;

    const updatedCartUI = mustache.render(cartUI, {
      ...cartState,
      has_multiple: function () {
        return this.quantity > 1;
      },
      price_total: function () {
        return this.variant.price * this.quantity;
      },
      price_each: function () {
        return this.variant.price;
      },
    });

    const storeDetails = JSON.parse(storeData);
    const updatedEmptyCartUI = mustache.render(cartEmptyUI, {
      ...storeDetails.settings,
    });

    cartElement.innerHTML = items.length ? updatedCartUI : updatedEmptyCartUI;
  }

  // Update Cart Values after Cart UI Displayed
  const cartTotalItems = document.querySelector(
    '[data-mvthru-cart-value="total-items"]',
  );

  const cartTotalPrice = document.querySelector(
    '[data-mvthru-cart-value="total-price"]',
  );

  if (cartTotalItems) {
    console.log("Total Items:", cartState);
    cartTotalItems.innerText = cartState.total?.items || 0;
  }

  if (cartTotalPrice) {
    cartTotalPrice.innerText = cartState.total?.price || 0;
  }
};

// Fetch cart data from the server
export const fetchCart = async () => {
  try {
    const response = await fetch("/api/cart");
    if (!response.ok) throw new Error("Failed to fetch cart data");

    const cartData = await response.json();
    if (cart) {
      cart.setState(cartData); // Set entire cart data
    }
  } catch (error) {
    console.error("Error fetching cart:", error);
  }
};

// Add item to cart
export const addItem = (item) => {
  cart.setState((currentCart) => {
    let updatedCart = { ...currentCart };
    let updatedItems = [...(currentCart?.items || [])];

    // Check if item already exists in the cart
    const existingItemIndex = updatedItems.findIndex(
      (i) => i.variant.id === item.variant.id,
    );

    if (existingItemIndex !== -1) {
      // Update quantity for existing item
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity:
          updatedItems[existingItemIndex].quantity + (item.quantity || 1),
      };
    } else {
      // Add new item to the cart
      updatedItems.push({ ...item, quantity: item.quantity || 1 });
    }

    return structureCart({ cart: updatedCart, items: updatedItems });
  });
};

// Remove item from cart
export const removeItem = (id) => {
  if (!cart) return;

  cart.setState((currentCart) => {
    const updatedItems = currentCart.items.filter((item) => item.id !== id);

    return structureCart({ cart: currentCart, items: updatedItems });
  });
};

// Edit item in the cart
export const editItem = (id, quantity) => {
  if (!cart) return;

  cart.setState((currentCart) => {
    const updatedItems = currentCart.items.map((item) =>
      item.id === id
        ? {
            ...item,
            quantity,
          }
        : item,
    );

    // Return the full updated cart state
    return structureCart({ cart: currentCart, items: updatedItems });
  });
};

// HELPERS
function sumKey(itemsArray, key) {
  if (!itemsArray || itemsArray.length === 0) {
    return 0;
  }

  return itemsArray.reduce((sum, item) => {
    const value = key.split(".").reduce((obj, k) => obj && obj[k], item);
    return sum + (typeof value === "number" ? value : 0);
  }, 0);
}

function calculateItemsTotal(items) {
  return items.reduce((total, item) => {
    const itemTotal = item.variant.price * item.quantity;
    return total + itemTotal;
  }, 0);
}

function structureCart({ cart, items = [] }) {
  if (!cart) return null;

  return {
    ...cart,
    items,
    total: {
      items: sumKey(items, "quantity") ?? 0,
      price: calculateItemsTotal(items) ?? 0,
    },
  };
}
